import { useMemo } from 'react';
import * as THREE from 'three';

interface Props {
  preset: string;
  intensity: number;
  fog: boolean;
  background: string;
}

/** 光照预设：环境光 + 主光 + 氛围点光 + 雾 */
const LIGHT_DATA: Record<string, { ambient: string; key: string; point?: string }> = {
  'soft-glow': { ambient: '#fff5e0', key: '#ffd9a0' },
  moonlight: { ambient: '#b8c8e8', key: '#8a9cc8', point: '#c8d8ff' },
  firefly: { ambient: '#ffe9b0', key: '#ffb347', point: '#ffd76e' },
  dusk: { ambient: '#f0c8a8', key: '#c98a5a' },
  aurora: { ambient: '#c8e8d8', key: '#7ac8a8', point: '#5ad0c8' },
  sunrise: { ambient: '#ffe8d0', key: '#ffb88a', point: '#ffd0a8' },
};

export function EditorLighting({ preset, intensity, fog, background }: Props) {
  const data = useMemo(
    () => LIGHT_DATA[preset] ?? LIGHT_DATA['soft-glow'],
    [preset]
  );
  const fogColor = useMemo(() => new THREE.Color(background), [background]);
  const ambientColor = useMemo(() => new THREE.Color(data.ambient), [data.ambient]);
  const keyColor = useMemo(() => new THREE.Color(data.key), [data.key]);
  const pointColor = useMemo(
    () => (data.point ? new THREE.Color(data.point) : null),
    [data.point]
  );

  return (
    <>
      <color attach="background" args={[background]} />
      {fog && <fog attach="fog" args={[fogColor, 12, 26]} />}

      <ambientLight color={ambientColor} intensity={0.55 * intensity} />
      <directionalLight
        color={keyColor}
        intensity={1.4 * intensity}
        position={[4, 6, 3]}
      />
      {pointColor && (
        <pointLight
          color={pointColor}
          intensity={1.2 * intensity}
          position={[-3, 1.5, 2]}
          distance={10}
        />
      )}
      {/* 底部补光，让角色轮廓清晰 */}
      <hemisphereLight
        args={['#ffffff', '#223055', 0.35 * intensity]}
      />
    </>
  );
}